import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, LogOut, Settings } from "lucide-react";
import WeatherTracker from "./WeatherTracker";
import { PushNotificationToggle } from "./PushNotificationManager";

const Navigation = ({ cartCount, onCartClick, onAuthClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { id: "roster", label: "ROSTER" },
    { id: "pricing", label: "PRICING" },
    { id: "media", label: "MEDIA" },
    { id: "gallery", label: "GALLERY" },
    { id: "form-analyzer", label: "AI ANALYZER" },
    { id: "schedule", label: "SCHEDULE" },
    { id: "leaderboard", label: "LEADERBOARD" },
    { id: "nfl-combine", label: "NFL COMBINE" },
    { id: "shop", label: "SHOP" },
    { id: "referral", label: "REFER" },
    { id: "community", label: "COMMUNITY" },
    { id: "about", label: "ABOUT" },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-void-black/95 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-[1400px] mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div 
            className="flex-shrink-0 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="nav-logo"
          >
            <img 
              src="/gt-logo.webp" 
              alt="GT Training" 
              className="h-10 md:h-14 w-auto object-contain drop-shadow-[0_0_10px_rgba(74,144,255,0.5)]"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="font-orbitron text-[10px] xl:text-xs text-neon-pink hover:text-white transition-colors tracking-wider drop-shadow-[0_0_8px_rgba(255,0,127,0.5)]"
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Admin Link - only for admin users */}
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1 px-2 py-1 bg-neon-pink/20 border border-neon-pink/50 text-neon-pink font-orbitron text-[10px] hover:bg-neon-pink hover:text-black transition-colors"
                data-testid="nav-admin-btn"
              >
                <Settings className="w-3 h-3" />
                ADMIN
              </button>
            )}

            {/* Cart */}
            {/* Weather Widget */}
            <div className="hidden xl:block">
              <WeatherTracker compact={true} />
            </div>
            
            {/* Push Notifications Toggle */}
            <PushNotificationToggle />

            {/* Cart */}
            <button
              onClick={onCartClick}
              className="relative p-1.5 text-neon-pink hover:text-white transition-colors"
              data-testid="nav-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-neon-blue text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Auth */}
            {user ? (
              <div className="flex items-center gap-2">
                <span className="hidden lg:block font-rajdhani text-xs text-neon-pink">
                  {user.name || user.email?.split('@')[0]}
                </span>
                <button
                  onClick={logout}
                  className="p-1.5 text-neon-pink hover:text-white transition-colors"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={onAuthClick}
                className="py-1.5 px-3 md:py-2 md:px-4 text-[10px] md:text-xs font-orbitron bg-gradient-to-r from-neon-blue to-neon-pink text-white rounded border border-white/20 hover:border-white/50 transition-all shadow-lg shadow-neon-pink/30"
                data-testid="nav-login-btn"
              >
                LOGIN
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-400 hover:text-neon-blue transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/5">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="font-orbitron text-sm text-neon-pink hover:text-white transition-colors tracking-wider text-left py-2 drop-shadow-[0_0_8px_rgba(255,0,127,0.5)]"
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              {user?.role === 'admin' && (
                <button
                  onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                  className="font-orbitron text-sm text-neon-blue hover:text-white transition-colors tracking-wider text-left py-2 flex items-center gap-2"
                  data-testid="mobile-nav-admin"
                >
                  <Settings className="w-4 h-4" />
                  ADMIN DASHBOARD
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
